import React, { useState, useEffect } from 'react';
import RWDropdownList from 'react-widgets/lib/DropdownList';
import _get from 'lodash/get';

import { client } from 'cccisd-apollo';
import { Field, CccisdSelect, CccisdFieldWrapper, CccisdWysiwyg } from 'cccisd-formik';
import IconQuestion from 'cccisd-icons/question4';
import Tooltip from 'cccisd-tooltip';
import style from '../style.css';

import assignmentsQuery from './assignments.graphql';
import surveysQuery from './surveys.graphql';
import variablesQuery from './variables.graphql';

const Variable = ({ localForm, initialWidget }) => {
    const [assignments, setAssignments] = useState([]);
    const [isLoadingAssignments, setIsLoadingAssignments] = useState(true);
    const [selectedAssignmentId, setSelectedAssignmentId] = useState(initialWidget?.assignmentId || '');

    const [surveys, setSurveys] = useState([]);
    const [isLoadingSurveys, setIsLoadingSurveys] = useState(true);
    const [selectedSurveyId, setSelectedSurveyId] = useState(initialWidget?.surveyId || '');

    const [variables, setVariables] = useState([]);
    const [isLoadingVariables, setIsLoadingVariables] = useState(true);

    useEffect(() => {
        async function getAssignments() {
            setIsLoadingAssignments(true);
            const resp = await client.query({
                query: assignmentsQuery,
                fetchPolicy: 'network-only',
            });

            const options = _get(resp, 'data.flows.assignmentList', []) || [];
            if (!options[0] || options[0].assignmentId !== '') {
                options.unshift({ assignmentId: '', label: '-- Select Quest --', disabled: true });
            }
            setAssignments(options);
            setIsLoadingAssignments(false);
        }

        getAssignments();
    }, []);

    useEffect(() => {
        async function getSurveys() {
            setIsLoadingSurveys(true);
            const resp = await client.query({
                query: surveysQuery,
                fetchPolicy: 'network-only',
                variables: {
                    assignmentId: selectedAssignmentId,
                },
            });

            const options = _get(resp, 'data.flows.surveyList', []) || [];
            if (!options[0] || options[0].surveyId !== '') {
                options.unshift({ surveyId: '', label: '-- Select Survey --', disabled: true });
            }

            setSurveys(options);
            setIsLoadingSurveys(false);
        }

        if (selectedAssignmentId) {
            getSurveys();
        }
    }, [selectedAssignmentId]);

    useEffect(() => {
        async function getVariables() {
            setIsLoadingVariables(true);
            const resp = await client.query({
                query: variablesQuery,
                variables: {
                    assignmentId: selectedAssignmentId,
                    surveyId: selectedSurveyId,
                },
                fetchPolicy: 'network-only',
            });

            setVariables(_get(resp, 'data.metrics.variableList', []) || []);
            setIsLoadingVariables(false);
        }

        if (selectedAssignmentId && selectedSurveyId) {
            getVariables();
        }
    }, [selectedAssignmentId, surveys, selectedSurveyId]);

    // handle case where survey or assignment has been deleted since report was created
    const isAssignmentValid =
        selectedAssignmentId && assignments.some(opt => Number(opt.assignmentId) === Number(selectedAssignmentId));
    const isSurveyValid = selectedSurveyId && surveys.some(opt => Number(opt.surveyId) === Number(selectedSurveyId));

    return (
        <>
            <Field name="assignmentId">
                {fieldProps => (
                    <CccisdSelect
                        {...fieldProps}
                        disabled={isLoadingAssignments}
                        label="Quest"
                        options={assignments.map(a => ({
                            value: a.assignmentId,
                            ...a,
                        }))}
                        onChange={event => {
                            const newVal = event.currentTarget.value;
                            setSelectedAssignmentId(newVal);
                            setSelectedSurveyId('');
                            localForm.setFieldValue('assignmentId', newVal);
                            localForm.setFieldValue('surveyId', '');
                            localForm.setFieldValue('variableId', '');
                            localForm.setFieldValue('questionText', '');
                        }}
                    />
                )}
            </Field>
            <Field name="surveyId">
                {fieldProps => (
                    <CccisdSelect
                        {...fieldProps}
                        disabled={!localForm.values.assignmentId || !isAssignmentValid || isLoadingSurveys}
                        label="Survey"
                        options={surveys.map(s => ({
                            value: s.surveyId,
                            ...s,
                        }))}
                        onChange={event => {
                            const newVal = event.currentTarget.value;
                            setSelectedSurveyId(newVal);
                            localForm.setFieldValue('surveyId', newVal);
                            localForm.setFieldValue('variableId', '');
                            localForm.setFieldValue('questionText', '');
                        }}
                    />
                )}
            </Field>
            <Field name="variableId">
                {fieldProps => (
                    <CccisdFieldWrapper
                        {...fieldProps}
                        label={
                            <span>
                                Variable{' '}
                                <Tooltip
                                    title={
                                        <div className={style.variableTooltip}>
                                            <h6>Only includes these types of variables:</h6>
                                            <ul>
                                                <li>Additional Coding</li>
                                                <li>Correct</li>
                                                <li>Flow Variable</li>
                                                <li>Formula</li>
                                                <li>Qualitative</li>
                                                <li>Quantitative</li>
                                            </ul>
                                        </div>
                                    }
                                >
                                    <span className={style.tooltipIcon}>
                                        <IconQuestion />
                                    </span>
                                </Tooltip>
                            </span>
                        }
                    >
                        <RWDropdownList
                            data={variables}
                            busy={!!(isAssignmentValid && isSurveyValid && isLoadingVariables)}
                            disabled={!isAssignmentValid || !isSurveyValid}
                            autoComplete="on"
                            onChange={val => {
                                localForm.setFieldValue('variableId', val.variableId);
                                const selectedVar =
                                    val.variableId &&
                                    variables.find(v => Number(v.variableId) === Number(val.variableId));

                                const defaultQuestionText = _get(selectedVar, 'content.title', '');
                                localForm.setFieldValue('questionText', defaultQuestionText);
                                localForm.setFieldValue('variable', selectedVar);
                            }}
                            value={variables.find(
                                v =>
                                    v?.variableId &&
                                    parseInt(v.variableId, 10) === parseInt(localForm.values.variableId, 10)
                            )}
                            dataKey="variableId"
                            textField="reportHeader"
                        />
                    </CccisdFieldWrapper>
                )}
            </Field>
            <Field
                name="questionText"
                label="Question Text"
                component={CccisdWysiwyg}
                type="input"
                disabled={!localForm.values.variableId}
            />
        </>
    );
};

export default Variable;
